import { createApp } from 'vue';
import { getMeasureLabel, getMeasureLevel } from '@/lib/typography';
import inputNumber from '@/components/input-number.vue';
import productDisplay from '@/components/product-display.vue';
import useVuelidate from '@vuelidate/core';
import { required, minValue, email } from '@vuelidate/validators';
import AnimatedModal from '@/components/AnimatedModal.vue';
import type { AnyType } from '@/lib/helpers';
import type { Measure } from '@/common/measure';
import { currency } from '@/lib/validators';
import type { CurrencyId } from '@/lib/currency';

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-layout]').forEach(ilayout => {
		const jsParams = JSON.parse(ilayout.getAttribute('data-layout')!);
		const app = createApp({
			setup() {
				return { v$: useVuelidate(), currency };
			},

			data() {
				const data = {
					showSettings: false,
					result: null,
					product: null,
					settings: null,
					currencies: null,
					sizes: { '': null },
					isAuthorized: null,
					user: {
						email: null,
						locationCode: null,
					},
				};
				return data;
			},

			validations() {
				const currentValidators: AnyType = {};

				if (this.size) {
					currentValidators.size = {};
					for (const i_size in this.size) {
						if (!this.size.hasOwnProperty(i_size)) {
							continue;
						}
						if (i_size === 'glueFlap') {
							continue;
						}
						currentValidators.size[i_size] = {
							required,
							between: minValue(1),
						};
					}
				}

				// FIXME проверки настроек для разных продуктов

				currentValidators.settings = {};
				switch (this.product) {
					case 'paper_bag':
						currentValidators.settings = {
							parts: {
								required,
							},
						};
						break;
				}

				switch (this.settings.bottomWidthCalc) {
					case 'percent':
						currentValidators.settings.bottomWidthCalcPercent = {
							required,
							between: minValue(50),
						};
						break;
					case 'absolute':
						currentValidators.settings.bottomWidthCalcAbsolute = {
							required,
							between: minValue(1),
						};
						break;
				}

				if (!this.isAuthorized) {
					currentValidators.user = {
						email: {
							required,
							email,
						},
					};
				}

				return currentValidators;
			},

			created() {
				this.product = jsParams['product'];
				this.needLocation = jsParams['needLocation'];
				this.size = jsParams['sizes'] || {};
				const settings = jsParams['settings'] || {};
				this.settings = settings;
				this.currencies = jsParams['currencies'] || {};
				this.isAuthorized = jsParams['isAuthorized'];
			},

			mounted() {
				if (this.needLocation) {
					this.$nextTick(() => {
						// костыль для совместимости с vue:
						// перемещаем script-шаблоны в узел scope, чтобы находились
						const root = document.getElementById('sls-layout');
						if (root) {
							const tpl_dropdown_item = document.querySelector('[data-template-id="bx-ui-sls-dropdown-item"]');
							if (tpl_dropdown_item) {
								root.insertAdjacentElement('afterbegin', tpl_dropdown_item);
							}
							const tpl_dropdown_error = document.querySelector('[data-template-id="bx-ui-sls-error"]');
							if (tpl_dropdown_error) {
								root.insertAdjacentElement('afterbegin', tpl_dropdown_error);
							}
						}

						const w = window as AnyType;
						const wbx = window.BX as AnyType;

						// отложенная инициализация
						wbx.locationsDeferred['layout']();

						// инициализация по событию
						wbx.onCustomEvent(document, 'eventInitLayoutLocation');

						const locationCode = this.user.locationCode || wbx.getCookie('layoutLocation');
						if (locationCode) {
							this.setLocationCode(locationCode);
						}
						w.onSelectLayoutLocation = (_locationId: AnyType, obLocationSelectorSteps: AnyType) => {
							const locationCode = obLocationSelectorSteps.getValue();
							this.user.locationCode = locationCode;
							BX.setCookie('layoutLocation', locationCode, { path: '/' });
						};

						if (locationCode == undefined) {
							fetch('./?model=location&action=read', {
								credentials: 'same-origin',
								method: 'GET',
							})
								.then(function (response) {
									return response.json();
								})
								.then(response => {
									if (typeof response['locationCode'] != 'undefined') {
										this.setLocationCode(response['locationCode']);
									}
								});
						}
					});
				}
			},

			methods: {
				layout() {
					this.result = null;
					this.v$.$touch();
					if (!this.canLayout) {
						return;
					}
					this.v$.$reset();

					const body = {
						settings: this.settings,
						size: this.size,
						user: this.user,
					};
					fetch('./?model=layout&action=create', {
						credentials: 'same-origin',
						method: 'POST',
						body: JSON.stringify(body),
					})
						.then(function (response) {
							return response.json();
						})
						.then(response => {
							let uid = null;
							if (typeof response['uid'] == 'string') {
								uid = response['uid'];
							}
							response['uid'] = uid;
							this.result = response;
						});
				},

				setLocationCode(locationCode: string) {
					const locationSelectors = (BX as AnyType).locationSelectors;
					if (locationSelectors) {
						locationSelectors['layout'].setValueByLocationCode(locationCode);
					}
				},

				getCurrencyCodeDisplay(currencyId: CurrencyId) {
					return (this.currencies[currencyId].formatString || '').replace('#', '');
				},

				getMeasureLevel(measure: Measure) {
					return getMeasureLevel(measure);
				},
				getMeasureLabel(measure: Measure) {
					return getMeasureLabel(measure);
				},
			},

			computed: {
				size: {
					get() {
						return this.sizes[''];
					},
					set(size) {
						this.sizes[''] = size;
					},
				},

				canLayout() {
					return !this.v$.size.$invalid && !this.v$.settings.$invalid && (this.isAuthorized || !this.v$.user.$invalid);
				},
			},
		});
		app.component('inputNumber', inputNumber);
		app.component('productDisplay', productDisplay);
		app.component('AnimatedModal', AnimatedModal);
		app.mount(ilayout);
	});
});
